/**
 * SE2 · Full-width header bar: the over-hero / solid switch (owner test, 2026-09-25).
 * Setting: sections/header.liquid "Header style" (se2_header_style = bar). Styles: the bar block at the foot of
 * assets/se2-header-pill.css.
 *
 * One job: put data-se2-scrolled on #header-component once the page has scrolled 24px, and take it off again
 * above that. The CSS reads it only on the homepage video hero, where the bar starts clear over the video and
 * turns into the solid ivory bar past the threshold; every other template is solid from the start and ignores it.
 *
 * Two scrollers, because assets/base.css moves the scroll at 990: below it the document scrolls, from it up
 * .page-wrapper does (html and body are overflow: hidden). Both are listened to, passively, and whichever has
 * moved counts. Reads are batched into one animation frame, and the attribute is only written when it changes,
 * so a long scroll costs one comparison per frame and no style recalculation.
 */
(() => {
  const THRESHOLD = 24;

  /** @type {HTMLElement | null} */
  let header = null;
  /** @type {HTMLElement | null} */
  let wrapper = null;
  let frame = 0;

  const scrolled = () => Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0) >= THRESHOLD;

  const update = () => {
    frame = 0;
    if (!header || !header.isConnected) return;
    if (header.dataset.se2HeaderStyle !== 'bar') {
      header.removeAttribute('data-se2-scrolled');
      return;
    }
    const next = scrolled();
    if (header.hasAttribute('data-se2-scrolled') !== next) header.toggleAttribute('data-se2-scrolled', next);
  };

  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };

  const bind = () => {
    header = document.getElementById('header-component');
    // The bar rules read the header's own attribute, but the over-hero state keys on the <html> copy the inline
    // script writes. If the theme editor swaps the section in without re-running that script, switching Header
    // style between pill and bar would leave the <html> copy stale until a reload, so it is mirrored here too.
    if (header) document.documentElement.setAttribute('data-se2-header-style', header.dataset.se2HeaderStyle || 'pill');
    const nextWrapper = document.querySelector('.page-wrapper');
    if (nextWrapper !== wrapper) {
      wrapper?.removeEventListener('scroll', schedule);
      wrapper = nextWrapper instanceof HTMLElement ? nextWrapper : null;
      wrapper?.addEventListener('scroll', schedule, { passive: true });
    }
    update();
  };

  window.addEventListener('scroll', schedule, { passive: true });
  // Crossing 990 swaps which of the two scrollers is live, and a restored page (bfcache) keeps its offset.
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('pageshow', schedule);
  // The theme editor re-renders the header section when its settings change: pick up the new element and style.
  document.addEventListener('shopify:section:load', bind);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
})();
